import { useMemo } from 'react';
import { useFragment } from '@apollo/client';
import type { AccountPlanAndTrialInfoFragment } from '@frameio/graphql-client/generated';
import { AccountPlanAndTrialInfo } from '@frameio/graphql-client/generated';
import { useActiveAccountId } from '../../contexts';
import { planPeriodKey, planTierKey } from '../constants';

type CurrentPlanTracking = {
  current_plan_cost: number;
  current_plan_id: string;
  current_plan_is_trial: boolean;
  current_plan_name: string;
  current_plan_period: string;
  current_plan_tier: string;
  current_plan_version: number;
};

export function isCurrentPlanDiscountOffer(planName?: string | null): boolean {
  // @todo (MON-1678): This is a temporary solution to identify the discount offer plan.
  // Replace with a check for subscription.offer_expires_at when available.
  return !!planName?.endsWith('save-35');
}

export function useCurrentPlanTracking(): CurrentPlanTracking | undefined {
  const accountId = useActiveAccountId() ?? '';
  const { complete, data } = useFragment<AccountPlanAndTrialInfoFragment>({
    fragment: AccountPlanAndTrialInfo,
    fragmentName: 'AccountPlanAndTrialInfo',
    from: {
      __typename: 'Account',
      id: accountId,
    },
  });

  const currentPlanTracking = useMemo(() => {
    const currentPlan = data?.subscription?.plan;

    if (!complete || !currentPlan) {
      return undefined;
    }

    return {
      current_plan_cost: currentPlan.cost,
      current_plan_id: currentPlan.id,
      current_plan_is_offer: isCurrentPlanDiscountOffer(currentPlan.name),
      current_plan_is_trial: !!data?.subscription.trial.isActive,
      current_plan_name: currentPlan.name,
      current_plan_period: planPeriodKey[currentPlan.period],
      current_plan_tier: planTierKey[currentPlan.tier],
      current_plan_version: currentPlan.version,
    };
  }, [complete, data]);

  return currentPlanTracking;
}
